/**
 * EDC-14 - Quick add to cart on product tiles.
 *
 * The buttons themselves are rendered server side by snippets/quick-add-button.liquid,
 * so filtered / paginated / AJAX re-rendered grids get them for free and no
 * MutationObserver is needed here. This file only wires up the click.
 *
 * Adding uses the same flow as the theme's own product form (assets/product.js):
 * POST to routes.cart_add_url with the cart sections, then dispatch
 * `switch:cart:afteradditem` so the Alpine cart drawer, line items, footer,
 * progress bar and item count all re-render from the returned section HTML.
 */
(() => {
  const BUTTON_SELECTOR = '.custom-add-to-cart';
  const LOADING_CLASS = 'loading';

  const getTheme = () => window.theme || {};

  const isCartPage = () => {
    const cartUrl = (getTheme().routes || {}).cart_url || '/cart';
    return window.location.pathname === cartUrl;
  };

  /**
   * Mirrors the section list assets/product.js asks for, so the same Alpine
   * listeners can morph the same markup.
   */
  const cartSections = () => {
    const cartType = (getTheme().settings || {}).cart_type;

    if (cartType === 'drawer' && !isCartPage()) {
      return 'drawer-cart-items,drawer-cart-footer,cart-item-count';
    }

    if (isCartPage()) {
      return 'cart-items,cart-footer,cart-item-count';
    }

    return 'cart-item-count';
  };

  const trackQuickAdd = (productTitle) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'GA4event',
      'ga4-event-name': 'cro_event',
      'ga4-event-p1-name': 'event_category',
      'ga4-event-p1-value': 'EDC14_QuickATC',
      'ga4-event-p2-name': 'event_label',
      'ga4-event-p2-value': productTitle,
    });
  };

  const requestConfig = () => {
    if (typeof fetchConfigDefaults === 'function') {
      return fetchConfigDefaults('application/javascript');
    }

    return {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json;',
        Accept: 'application/javascript',
      },
    };
  };

  const setLoading = (button, loading) => {
    button.classList.toggle(LOADING_CLASS, loading);
    button.disabled = loading;

    if (loading) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  };

  const addToCart = async (button) => {
    const variantId = button.getAttribute('data-variant-id');

    if (!variantId || button.classList.contains(LOADING_CLASS)) return;

    setLoading(button, true);
    trackQuickAdd(button.getAttribute('data-product-title') || '');

    const config = requestConfig();

    config.body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: cartSections(),
      sections_url: window.location.pathname,
    });

    const endpoint = (getTheme().routes || {}).cart_add_url || '/cart/add';

    try {
      const response = await fetch(endpoint, config);
      const data = await response.json();

      // Shopify returns a `status` key (422 etc.) when the add failed.
      if (data && data.status) {
        console.error(
          '[quick-add]',
          data.description || data.message || data.status
        );
        return;
      }

      document.body.dispatchEvent(
        new CustomEvent('switch:cart:afteradditem', {
          bubbles: true,
          detail: { response: data },
        })
      );
    } catch (error) {
      console.error('[quick-add]', error);
    } finally {
      setLoading(button, false);
    }
  };

  // Capture phase: the tile has a full-size <a class="tile-link"> overlay and
  // may have an Alpine quick-view handler. Stopping here keeps the click ours.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!target || typeof target.closest !== 'function') return;

      const button = target.closest(BUTTON_SELECTOR);

      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      addToCart(button);
    },
    true
  );
})();
